import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  StatementCapture, analyseApplicationStatements, retryApplicationStatement, statementError,
} from '../../../services/statementIq';
import type { DocumentReference } from '../../../services/schemas/loanServiceSchemas';
import { BUSY, figures, statementFigures, statusLabel } from './bankStatementAnalysis';

const money = (n?: number) => (n === undefined ? '—' : n.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' }));
const storedId = (d: DocumentReference) => d.documentKey || d.id;

interface Props {
  applicationId: string;
  /** The application's current bank statements, from Upload Documents */
  documents: DocumentReference[];
  captures?: StatementCapture[];
  /** Settings > StatementIQ is switched on */
  enabled?: boolean;
  onChanged: () => void;
}

/**
 * Section 1's evidence: each bank statement as StatementIQ analysed it, and the averages the section starts
 * from. The flags support the assessor's review of cash-flow behaviour (manual 1.1, steps 4 and 5).
 */
const BankStatementAnalysisPanel: React.FC<Props> = ({ applicationId, documents, captures = [], enabled, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const analysed = new Set(captures.map(c => c.documentId).filter(Boolean));
  const waiting = documents.filter(d => !analysed.has(storedId(d)));
  const all = figures(captures);
  const doc = (c: StatementCapture) => documents.find(d => storedId(d) === c.documentId);
  const warnings = Array.from(new Set(captures.flatMap(c => c.result?.warnings || [])));
  const fraud = captures.filter(c => c.result?.fraud?.summary || (c.result?.fraud?.risk_level && c.result.fraud.risk_level.toUpperCase() !== 'LOW'));

  const analyse = async () => {
    setBusy(true);
    try {
      await analyseApplicationStatements(applicationId);
      setAttempted(true);
      toast.info('Bank statements sent to StatementIQ. The figures fill in when the analysis is ready.');
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || statementError(e));
    } finally {
      setBusy(false);
    }
  };
  const retry = async (c: StatementCapture) => {
    try { await retryApplicationStatement(applicationId, c.id); onChanged(); } catch (e: any) { toast.error(statementError(e)); }
  };

  if (!documents.length && !captures.length) {
    return <p className="small text-muted mb-3">No bank statements uploaded at Upload Documents. Complete the figures below from the statements.</p>;
  }

  return (
    <div className="border rounded p-2 mb-3 small" aria-label="Bank statements from StatementIQ">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <strong>Bank statements (StatementIQ)</strong>
        {enabled && waiting.length > 0 && !attempted && (
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={analyse}>
            {busy ? 'Sending…' : `Analyse ${waiting.length} bank statement${waiting.length === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
      {enabled === false && waiting.length > 0 && (
        <p className="text-muted mb-2">StatementIQ is switched off in Settings. Complete the figures below from the statements.</p>
      )}
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-2">
          <thead className="text-muted">
            <tr><th>Statement</th><th>Status</th><th>Months</th><th>Avg credits</th><th>Avg debits</th><th>Returned debit orders</th><th>Lowest balance</th><th>Fraud risk</th></tr>
          </thead>
          <tbody>
            {captures.map(c => {
              const f = statementFigures(c);
              const d = doc(c);
              return (
                <tr key={c.id}>
                  <td>{d?.name || c.name}{d?.period ? <span className="text-muted"> · {d.period}</span> : null}</td>
                  <td>
                    {statusLabel(c.status)}
                    {c.status === 'TIMED_OUT' && enabled && <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => retry(c)}>Check again</button>}
                  </td>
                  <td>{f.months || '—'}</td>
                  <td>{money(f.inflows)}</td>
                  <td>{money(f.outflows)}</td>
                  <td>{f.returnedDebitOrders ?? '—'}</td>
                  <td>{money(f.lowestBalance)}</td>
                  <td title={c.result?.fraud?.recommendation}>{c.result?.fraud?.risk_level || '—'}</td>
                </tr>
              );
            })}
            {waiting.map(d => (
              <tr key={storedId(d)} className="text-muted">
                <td>{d.name || 'Bank statement'}{d.period ? ` · ${d.period}` : ''}</td>
                <td colSpan={7}>{attempted ? 'Not analysed: only PDF statements can be read' : 'Not analysed yet'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {captures.some(c => BUSY.includes(c.status)) && <p className="text-muted mb-1" role="status">Analysis in progress; this updates by itself.</p>}
      {all.months > 0 && (
        <p className="mb-1">
          Inflows and outflows below start from the average of {all.months} month{all.months === 1 ? '' : 's'} across {all.statements} statement{all.statements === 1 ? '' : 's'}.
          They still include transfers, loan proceeds and once-off items: adjust them to trading inflows and operating outflows (manual 1.1).
        </p>
      )}
      {warnings.length > 0 && <ul className="mb-1 text-warning-emphasis">{warnings.map(w => <li key={w}>{w}</li>)}</ul>}
      {fraud.map(c => (
        <p key={c.id} className="mb-1 text-danger">
          {doc(c)?.name || c.name}: fraud risk {c.result?.fraud?.risk_level || 'flagged'}{c.result?.fraud?.summary ? `: ${c.result.fraud.summary}` : ''}
        </p>
      ))}
    </div>
  );
};

export default BankStatementAnalysisPanel;
